import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Button, Col, Form, Row, Typography } from 'antd';

import { useDisclosure, useRouteMatchParam } from '@/hooks';

import {
  useCompanyStockConfiguration,
  useUpdateCompanyStockConfigurationFixedCost,
} from '@/core/domain/stock/Configuration/Configuration.hooks';

import { PageProps } from '@/types/PageProps';
import { FloatingFormItem, InputDecimal } from '@/components/_common';
import FixedCostModal from './FixedCostModal';

const { Title } = Typography;

function CompanyProfitCostCompositionPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<{
    fixedCost: number;
    fixedProfitMargin: number;
  }>();
  const companyId = +useRouteMatchParam('companyId');

  const [FixedCostOpen, setFixedCostActions] = useDisclosure();

  const { mutateAsync: FixedCostMutateAsync, isLoading: FixedCostIsLoading } =
    useUpdateCompanyStockConfigurationFixedCost();

  const { data, isLoading } = useCompanyStockConfiguration(companyId);

  const fixedCost = Form.useWatch('fixedCost', form) / 100;
  const fixedProfitMargin = Form.useWatch('fixedProfitMargin', form) / 100;

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        fixedCost: data.result.fixedCost * 100,
        fixedProfitMargin: data.result.fixedProfitMargin * 100,
      });
    }
  }, [data]);

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <div className="page">
        <div className="page-header" style={{ marginBottom: '24px' }}>
          <Title level={3}>Composição de Custo e Lucro</Title>
        </div>

        <Form form={form}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                noStyle
                name="fixedCost"
                label={intl.formatMessage({
                  id: 'companies.settings.priceComposition.domain.fixedCost',
                })}
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    max: 100,
                    message: intl.formatMessage({
                      id: 'commons.form.errors.percentageField',
                    }),
                  },
                ]}
                initialValue={0}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <Button
                type="primary"
                onClick={async () =>
                  await FixedCostMutateAsync({
                    companyId,
                    fixedCost,
                  })
                }
                loading={FixedCostIsLoading || isLoading}
              >
                Salvar Custo
              </Button>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                name="fixedProfitMargin"
                label={intl.formatMessage({
                  id: 'companies.settings.priceComposition.domain.fixedProfitMargin',
                })}
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    message: intl.formatMessage({
                      id: 'commons.form.errors.percentageField.min',
                    }),
                  },
                  {
                    type: 'number',
                    max: 1000,
                    message: intl.formatMessage({
                      id: 'commons.form.errors.percentageField.max',
                    }),
                  },
                ]}
                initialValue={0}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <Button
                type="primary"
                onClick={setFixedCostActions.open}
                loading={isLoading}
              >
                Salvar Lucro
              </Button>
            </Col>
          </Row>
        </Form>

        <FixedCostModal
          fixedProfitMargin={fixedProfitMargin}
          onCancel={setFixedCostActions.toggle}
          open={FixedCostOpen}
        />
      </div>
    </>
  );
}

export default CompanyProfitCostCompositionPage;
